import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import _ from "lodash";
import { SearchFieldNames } from "rl-common/components/entities/entity-search/query.models";
import { GridDataSourceBuilder } from "rl-common/components/grid/datasource/builders/grid-datasource-builder";
import { IGridFetchResults } from "rl-common/components/grid/datasource/grid-datasource.models";
import { SearchGridDataSource } from "rl-common/components/grid/datasource/search-grid.datasource";
import { GridTableComponent } from "rl-common/components/grid/grid-table/grid-table.component";
import { GridColumn } from "rl-common/components/grid/models/grid-column";
import { GridLayoutOptions } from "rl-common/components/grid/models/grid-layout-options";
import { LoaderComponent } from "rl-common/components/panel/loader/loader.component";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { CharDataType } from "rl-common/consts";
import { IEntitySearchDoc } from "rl-common/models/i-entity-search-doc";
import { IKeyValuePair } from "rl-common/models/i-key-value-pair";
import { ISearchRequestOptions } from "rl-common/models/i-search-request-options";
import { MultipleIndicator } from "rl-common/models/multiple-indicator";
import { CharTypeNamePipe } from "rl-common/pipes/char-type-name.pipe";
import { ISourceField } from "rl-common/services/entity-config/entity-config.models";
import { EntityService } from "rl-common/services/entity/entity.service";
import { SearchService } from "rl-common/services/search/search.service";
import { QueryUtil } from "rl-common/utils";
import { SolrUtil } from "rl-common/utils/solr-util";
import { BehaviorSubject, Subscription } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";

interface IEntitySearchDocCount extends IEntitySearchDoc {
	count: number;
}

@Component({
	selector: "rl-source-field-uniqueness-validation-panel",
	templateUrl: "./source-field-uniqueness-validation-panel.component.html",
	styleUrls: ["./source-field-uniqueness-validation-panel.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CharTypeNamePipe, LoaderComponent, GridTableComponent, FormsModule, TextInputComponent]
})
export class SourceFieldUniquenessValidationPanelComponent implements OnInit, OnDestroy {

	@Input()
	sourceField: ISourceField = null;

	isLoading: boolean = false;
	subs: Subscription[] = [];
	totalCounts: number = null;
	dataSource: SearchGridDataSource<IEntitySearchDoc>;
	counts$ = new BehaviorSubject<IKeyValuePair[]>([]);
	onlyShowDuplicates = true;
	fieldValue: string = "";

	layoutOptions: GridLayoutOptions = {
		maxHeight: 310
	};

	constructor(
		private readonly _entityService: EntityService,
		private readonly _searchService: SearchService,
		private readonly _gridDataSourceBuilder: GridDataSourceBuilder
	) { }

	ngOnInit(): void {
		const sub = this.getCounts$().subscribe();
		this.subs.push(sub);
	}

	ngOnDestroy(): void {
		this.subs.forEach(sub => sub.unsubscribe());
	}

	getDuplicateEntries() {
		this.isLoading = true;
		const term = SolrUtil.getSolrLabel(this.sourceField.mergeTag, this.sourceField.dataTypeID, this.sourceField.multipleIndicator === MultipleIndicator.Multiple);

		const fixedColumns: GridColumn<IEntitySearchDocCount>[] = [
			{
				key: "id",
				headerName: "ID",
				renderer: "link_id",
				width: "min-content",
				getCellData: (doc) => doc,
				sortKey: "recordid",
				locked: true
			},
			{
				key: "name",
				headerName: "Name",
				width: "max-content",
				renderer: "link_name",
				sortKey: "title_sort",
				getCellData: (doc) => doc
			},
			{
				key: "template",
				headerName: "Template",
				width: "minmax(auto, 200px)",
				renderer: "text",
				sortKey: "template",
				getCellData: (doc) => doc.template
			}, {
				key: "target",
				headerName: this.sourceField.mergeTag,
				width: "minmax(auto, 200px)",
				renderer: "text",
				sortKey: "target",
				getCellData: (doc) => {
					return doc.characteristics[term];
				}
			}, {
				key: "count",
				headerName: "Count",
				width: "minmax(auto, 100px)",
				renderer: "text",
				sortKey: "count",
				getCellData: doc => doc.count
			}
		];
		const columnStrategy = this._gridDataSourceBuilder.columnStrategies.searchDocColumnStrategy(this.sourceField.charTypeID, null, null)
			.setFixedColumns(fixedColumns);
		this.dataSource = this._gridDataSourceBuilder.searchGridDataSource<IEntitySearchDoc>((rowData) => rowData.recordID)
			.withColumnStrategy(columnStrategy)
			.withFetchFn(ds => {
				let query = QueryUtil.$is_not_null(term);
				const options: ISearchRequestOptions = {
					facetFields: [this.sourceField.mergeTag + "_str"],
					sortDir: ds.sortDir$.value,
					sortField: ds.sortKey$.value as string,
					start: ds.rowOffset$.value,
					rows: ds.rowCount$.value,
					filterQueries: []
				};
				if (this.fieldValue) {
					if (this.sourceField.mergeTag === "entity_title") {
						query = QueryUtil.$is_not_null(SearchFieldNames.Entity.titleSort);
						options.filterQueries.push(QueryUtil.$contains(SearchFieldNames.Entity.titleSort, this.fieldValue));
					} else {
						options.filterQueries.push(QueryUtil.$contains(term, this.fieldValue));
					}
				}

				return this._entityService.getUniquenessCounts(this.sourceField.sourceFieldID, this.sourceField.charTypeID, "", query, options)
					.pipe(switchMap(counts => {
						let modCounts = counts;
						if (this.onlyShowDuplicates) {
							modCounts = counts.filter(c => c.value > 1)
							const vals = modCounts.map(kvp => kvp.key);
							if (this.sourceField.mergeTag !== "entity_title") {
								options.filterQueries.push(QueryUtil.$eq_any(term, vals));
							}
						}
						return this._searchService.search(this.sourceField.charTypeID, "", query, options)
							.pipe(map(results => {
								const docs = results.documents.map(doc => {
									const charValue = doc.characteristics[term];
									const co = counts.find(c => {
										if (this.sourceField.dataTypeID == CharDataType.Number) {
											return +c.key === +charValue;
										}
										return c.key === charValue;
									});
									const docCount: IEntitySearchDocCount = { count: co?.value, ...doc };
									return docCount;
								})
								const r: IGridFetchResults<IEntitySearchDoc> = {
									rowCount: results.numFound,
									rowData: docs,
								};
								this.isLoading = false;
								return r;
							}))
					}));
			});
		const sub = this.dataSource.fetchRows().subscribe();
		this.subs.push(sub);
	}

	searchByValue$(ds: SearchGridDataSource<IEntitySearchDoc, unknown>, term: string, fieldValue: string) {
		const options: ISearchRequestOptions = {
			facetFields: [term],
			sortDir: ds.sortDir$.value,
			sortField: ds.sortKey$.value as string,
			filterQueries: []
		};
		const query = QueryUtil.$is_not_null(term);
		if (fieldValue) {
			options.filterQueries.push(QueryUtil.$contains(this.sourceField.mergeTag, fieldValue));
		}
		options.start = ds.rowOffset$.value;
		options.rows = ds.rowCount$.value;
		return this._searchService.search(this.sourceField.charTypeID, "", query, options)
			.pipe(map(results => {
				const docs = results.documents.map(doc => {
					// TODO: Figure out what to do with count here. It can't be guaranteed. 
					const docCount: IEntitySearchDocCount = { count: 1, ...doc };
					return docCount;
				})
				const r: IGridFetchResults<IEntitySearchDoc> = {
					rowCount: results.numFound,
					rowData: docs,
				};
				this.isLoading = false;
				return r;
			}))
	}

	getCounts$() {
		const options: ISearchRequestOptions = {
			facetFields: [this.sourceField.mergeTag + "_str"],
			filterQueries: []
		};
		return this._entityService.getUniquenessCounts(this.sourceField.sourceFieldID, this.sourceField.charTypeID, "", {}, options)
			.pipe(tap(counts => {
				this.counts$.next(counts);
				this.totalCounts = _.sumBy(counts, c => c.value);
			}));
	}

	validateUniqueness() {
		this.getDuplicateEntries();
	}
}